<template>
    <div class="orders-pivot">
        <h1>Commandes jusqu'au {{pivot.endDate | mysqlDateToFull}}</h1>

        <b-button v-if="!completedRecipesVisible" v-on:click="showCompletedRecipes()" variant="primary">Montrer recettes terminées</b-button>
        <b-button v-if="completedRecipesVisible" v-on:click="hideCompletedRecipes()" variant="primary">Masquer recettes terminées</b-button>
        
        <div class="pivot-tables mt-4">
            <!-- Frozen columns -->
            <div class="frozen-colums">
                <div class="table-responsive">
                    <table class="table table-sm font-weight-bold">
                        <!-- Column titles -->
                        <thead>
                            <tr class="headers">
                                <th>Client</th>
                                <th >Date de livraison</th>
                                <th>Référence</th>
                            </tr>
                        </thead>

                        <!-- Table rows -->
                        <tbody>
                            <tr v-for="offer in pivot.offers" :key="offer.id" :offer="offer" class="data-row">
                                <td class="pr-2">
                                    <a v-bind:href="'/customer/' + offer.customer.id + '/show'" target="_blank">
                                        {{offer.customer.firstname}} {{offer.customer.lastname}}
                                        <b-icon icon="box-arrow-up-right" font-scale="1.2"></b-icon>
                                    </a>
                                    </td>
                                <td class="pr-2">{{offer.delivery_date | mysqlDateToShort}}</td>
                                <td class="pr-2">
                                    <a v-bind:href="'/offer/' + offer.id + '/show'" target="_blank">
                                        {{offer.invoiceNumber}}
                                        <b-icon icon="box-arrow-up-right" font-scale="1.2"></b-icon>
                                    </a>
                                </td>
                            </tr>
                        </tbody>

                        <!-- Last row with totals -->
                        <tfoot>
                            <tr class="totals">
                                <td>Total</td>
                                <td></td>
                                <td class="foot-cell">
                                    <div class="text-right font-italic">Commande</div>
                                    <div class="text-right font-italic">Stock</div>
                                    <div class="text-right font-italic">A préparer</div>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            <!-- Scrolling columns -->
            <div class="scroll-columns">
                <div>
                    <table class="table table-sm">
                        <!-- Column titles -->
                        <thead>
                            <tr class="headers"  is="transition-group" name="fade">
                                <th
                                    v-for="recipe in pivot.recipes" :key="recipe.id" :recipe="recipe" 
                                    v-bind:class="{completed: !recipe.missingQuantity}"
                                    v-show="completedRecipesVisible || recipe.missingQuantity"
                                    class="text-center"
                                >
                                    <a v-bind:href="'/recipe/' + recipe.id" target="_blank">
                                        {{recipe.name.substring(0, 50)}}
                                        <span v-if="recipe.countRecipeRecipe > 0" class="explain"> Kit</span>
                                        <b-icon icon="box-arrow-up-right" font-scale="1.2"></b-icon>
                                    </a>
                                </th>
                            </tr>
                        </thead>

                        <!-- Table rows -->
                        <tbody>
                            <tr v-for="offer in pivot.offers" :key="offer.id" :offer="offer" class="data-row" is="transition-group" name="fade">
                                <td
                                    v-for="(recipe, recipe_key) in pivot.recipes" :key="recipe.id" :recipe="recipe" 
                                    v-bind:class="{completed: !recipe.missingQuantity}"
                                    v-show="completedRecipesVisible || recipe.missingQuantity"
                                    class="text-center"
                                >
                                        <a v-if="offer.recipes[recipe_key]" v-bind:href="'/recipe/requiredingredients/' + recipe.id + '/' + offer.recipes[recipe_key].nonReadyQuantity">
                                            {{offer.recipes[recipe_key].nonReadyQuantity}}
                                        </a>
                                    </td>
                                </tr>
                        </tbody>

                        <!-- Last row with totals -->
                        <tfoot>
                            <tr class="totals" is="transition-group" name="fade">
                                <td
                                    v-for="(recipe, recipe_key) in pivot.recipes" :key="recipe.id" :recipe="recipe" 
                                    v-bind:class="{completed: !recipe.missingQuantity}"
                                    v-show="completedRecipesVisible || recipe.missingQuantity"
                                    class="text-center foot-cell"
                                >
                                    <div>{{parseFloat(recipe.nonReadyQuantity)}}</div>
                                    <div>{{parseFloat(recipe.inventoryQuantity)}}</div>
                                    <div v-if="recipe.missingQuantity > 0">
                                        <b-form-input v-model="recipe.producedQuantity" size="sm" class="produced-quantity text-center" type="number"></b-form-input>
                                        <span 
                                            v-on:click="addProduction(recipe_key, recipe.id, recipe.producedQuantity)"
                                            class="oi oi-circle-check circle-check" 
                                            aria-hidden="true" style="color: green;"
                                        >
                                        </span>
                                        <a v-bind:href="'/recipe/requiredingredients/' + recipe.id + '/' + recipe.missingQuantity">
                                            <span class="oi oi-browser browser" aria-hidden="true" style="color: blue;"></span>
                                        </a>
                                    </div>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {  
        props: ['days'],
        created() {
            this.completedRecipesVisible = false;
            this.read();
        },
        data() {
            return {
                pivot: [],
                completedRecipesVisible: false,
            }
        },
        methods: {
            read() {
                window.axios.get('/offer/productionPivotData/' + this.days).then(response => {
                    this.pivot = response.data;
                });
            },
            showCompletedRecipes() {
                this.completedRecipesVisible = true;
            },
            hideCompletedRecipes() {
                this.completedRecipesVisible = false;
            },
            addProduction(recipeKey, recipeId, quantity) {
                window.axios.post('/recipe/markPrepared', {recipeId: recipeId, quantity: quantity}).then(response => {
                    this.pivot.recipes[recipeKey].inventoryQuantity = response.data.inventoryQuantity;
                    this.pivot.recipes[recipeKey].missingQuantity = Math.max(0, this.pivot.recipes[recipeKey].nonReadyQuantity -  this.pivot.recipes[recipeKey].inventoryQuantity);
                    this.pivot.recipes[recipeKey].producedQuantity = this.pivot.recipes[recipeKey].missingQuantity;
                })
            },
        },
        filters: {
            mysqlDateToFull: function (value) {
                if (!value) return '';
                return mysqlDateToFull(value);
            },
            mysqlDateToShort: function (value) {
                if (!value) return '';
                return mysqlDateToShort(value);
            }
        }
    }
</script>

<style scoped>
    .produced-quantity {
        width: 3rem;
        margin: auto;
    }

    .foot-cell {
        height: 7rem;
    }
</style>